import { create } from 'zustand'
import {
  rolePermissionsService,
  type AccessScope,
  type Module,
  type PermissionType,
  type RolePermissions,
} from '@/services/rolePermissionsService'
import { hasModuleView, isPlatformAdmin } from '@/lib/dashboardAccess'
import { useAuthStore } from '@/stores/authStore'

interface PermissionsState {
  permissions: RolePermissions | null
  moduleScopes: Partial<Record<Module, AccessScope>>
  role: string | null
  isLoading: boolean
  loadedForShopId: number | null
  loadPermissions: (shopId: number) => Promise<void>
  clear: () => void
  canView: (module: Module) => boolean
  can: (module: Module, type: PermissionType) => boolean
  getAccessScope: (module: Module) => AccessScope
}

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  permissions: null,
  moduleScopes: {},
  role: null,
  isLoading: false,
  loadedForShopId: null,

  loadPermissions: async (shopId: number) => {
    const { loadedForShopId, isLoading } = get()
    if (isLoading || loadedForShopId === shopId) return

    set({ isLoading: true })
    try {
      const data = await rolePermissionsService.getMyPermissions()
      set({
        permissions: data.permissions,
        moduleScopes: data.module_scopes ?? {},
        role: data.role,
        loadedForShopId: shopId,
        isLoading: false,
      })
    } catch (error) {
      console.error('Failed to load permissions:', error)
      set({
        permissions: null,
        moduleScopes: {},
        role: null,
        // Leave unloaded so a later attempt (or remount) can retry after token/shop switch.
        loadedForShopId: null,
        isLoading: false,
      })
    }
  },

  clear: () => {
    set({
      permissions: null,
      moduleScopes: {},
      role: null,
      isLoading: false,
      loadedForShopId: null,
    })
  },

  canView: (module: Module) => {
    const { permissions, role } = get()
    const authRole = useAuthStore.getState().user?.role
    return hasModuleView(permissions, module, role ?? authRole ?? undefined)
  },

  can: (module: Module, type: PermissionType) => {
    const { permissions, role } = get()
    // Session role fallback keeps platform admins unblocked if /me permissions failed mid-act-as.
    const authRole = useAuthStore.getState().user?.role
    const effectiveRole = role ?? authRole ?? undefined
    if (isPlatformAdmin(effectiveRole)) return true
    return permissions?.[module]?.[type] === true
  },

  getAccessScope: (module: Module) => {
    const { moduleScopes, role } = get()
    const authRole = useAuthStore.getState().user?.role
    const effectiveRole = role ?? authRole ?? undefined
    if (isPlatformAdmin(effectiveRole)) {
      return module === 'customers' ? 'full_contact' : 'all'
    }
    if (moduleScopes[module]) return moduleScopes[module]!
    return module === 'customers' ? 'no_contact' : 'own'
  },
}))
